import { Grid, Row, Col, Form, Divider, Schema, Text, Whisper, Tooltip, Stack, FlexboxGrid } from "rsuite";
import FormTextField from "../../form/FormTextField";
import React, { useMemo, useState, useEffect, forwardRef, useCallback } from "react";
import dayjs from "dayjs";
import { currencyPattern } from "../../../common/regex";
import { useDispatch, useSelector } from "react-redux";
import { useImperativeHandle, memo } from "react";
import {
  selectConfigExpressTaxDeductionsLabel,
  selectConfigExpressTaxDeductionsDescription,
  selectConfigExpressTaxGrossReceiptsLabel,
  selectConfigExpressTaxGrossReceiptsDescription,
  selectConfigExpressTaxTaxableReceiptsLabel,
  selectConfigExpressTaxHidePropertyOccupancySection,
  selectConfigExpressTaxHasCollectionAgreement,
  deriveReportRevenuePlatformsThatRemitLabel,
  deriveReportRevenuePlatformsThatDoNotRemitLabel,
  selectConfigExpressTaxEnableDeductions
} from "../../../store/slices/config/configSelectors";
import InfoRoundIcon from '@rsuite/icons/InfoRound'
import { convertNumberToCurrency, getAddressWithUnitNumber, isNullUndefinedOrEmptyString } from "../../../common/utils";
import { updateTaxFlowData } from "../../../store/slices/tax/taxSlice";

const ReportRevenueInput = forwardRef(({ license, period }, ref) => {
  const dispatch = useDispatch();

  const salesLabel = useSelector(selectConfigExpressTaxGrossReceiptsLabel);
  const salesDescription = useSelector(selectConfigExpressTaxGrossReceiptsDescription);
  const deductionsLabel = useSelector(selectConfigExpressTaxDeductionsLabel);
  const deductionsDescription = useSelector(selectConfigExpressTaxDeductionsDescription);
  const taxableReceitpsLabel = useSelector(selectConfigExpressTaxTaxableReceiptsLabel);
  const hidePropertyOccupancy = useSelector(selectConfigExpressTaxHidePropertyOccupancySection);
  const hasCollectionAgreement = useSelector(selectConfigExpressTaxHasCollectionAgreement);
  const showDeductions = useSelector(selectConfigExpressTaxEnableDeductions);
  const platformsThatRemitLabel = useSelector(deriveReportRevenuePlatformsThatRemitLabel);
  const platformsThatDoNotRemitLabel = useSelector(deriveReportRevenuePlatformsThatDoNotRemitLabel);

  const [formError, setFormError] = useState({});
  const [formValues, setFormValues] = useState({});

  const { startDate, endDate } = { ...period };

  const formModel = useMemo(() => {
    let model = {
      salesOtherPlatforms: isNullUndefinedOrEmptyString(license.salesOtherPlatforms) ? '' : license.salesOtherPlatforms,
      taxableReceiptsOtherPlatforms: isNullUndefinedOrEmptyString(license.taxableReceiptsOtherPlatforms) ? '' : license.taxableReceiptsOtherPlatforms,
      daysAvailable: isNullUndefinedOrEmptyString(license.daysAvailable) ? '' : license.daysAvailable,
      daysOccupied: isNullUndefinedOrEmptyString(license.daysOccupied) ? '' : license.daysOccupied
    };

    if (hasCollectionAgreement) {
      model.salesPlatformsThatRemit = isNullUndefinedOrEmptyString(license.salesPlatformsThatRemit) ? '' : license.salesPlatformsThatRemit;
      model.taxableReceiptsPlatformsThatRemit = isNullUndefinedOrEmptyString(license.taxableReceiptsPlatformsThatRemit) ? '' : license.taxableReceiptsPlatformsThatRemit;
    }

    if (showDeductions) {
      if (hasCollectionAgreement) {
        model.deductionsPlatformsThatRemit = isNullUndefinedOrEmptyString(license.deductionsPlatformsThatRemit) ? '' : license.deductionsPlatformsThatRemit;
      }

      model.deductionsOtherPlatforms = isNullUndefinedOrEmptyString(license.deductionsOtherPlatforms) ? '' : license.deductionsOtherPlatforms;
    }

    return model;
  }, [hasCollectionAgreement, showDeductions, license]);

  useEffect(() => {
    setFormValues(formModel);
  }, [formModel, setFormValues])

  const calculateTaxableReceipts = useCallback((sales, deductions) => {
    if (!isNullUndefinedOrEmptyString(sales)) {
      const salesNum = parseFloat(sales) || 0;
      const deductionsNum = parseFloat(deductions) || 0;

      return salesNum - deductionsNum;
    }
    else {
      return '';
    }
  }, []);

  const daysInPeriod = useMemo(() => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const diff = end.diff(start, 'day') + 1; // Adding 1 to include both start and end days
    return diff
  }, [startDate, endDate]);

  // Validation
  const { NumberType } = Schema.Types
  const invalidCurrencyErrorMessage = 'Invalid currency format. Enter a positive number with up to two decimal places.';
  const greaterThanZeroErrorMessage = 'Value must be greater than or equal to 0';
  const requiredErrorMessage = 'A value is required'
  const numberErrorMessage = 'The value input must be a number'
  const intgerErrorMessage = 'The value input must be a whole number'

  const validationModel = {
    salesPlatformsThatRemit:
      NumberType(numberErrorMessage)
        .isRequired(requiredErrorMessage)
        .min(0, greaterThanZeroErrorMessage)
        .pattern(currencyPattern, invalidCurrencyErrorMessage),
    deductionsPlatformsThatRemit:
      NumberType(numberErrorMessage)
        .isRequired(requiredErrorMessage)
        .min(0, greaterThanZeroErrorMessage)
        .pattern(currencyPattern, invalidCurrencyErrorMessage)
        .addRule((value, data) => {
          if (parseFloat(value) > parseFloat(data.salesPlatformsThatRemit)) {
            return false;
          }
          return true;
        }, `Deductions can't be more than sales`),
    salesOtherPlatforms:
      NumberType(numberErrorMessage)
        .isRequired(requiredErrorMessage)
        .min(0, greaterThanZeroErrorMessage)
        .pattern(currencyPattern, invalidCurrencyErrorMessage),
    deductionsOtherPlatforms:
      NumberType(numberErrorMessage)
        .isRequired(requiredErrorMessage)
        .min(0, greaterThanZeroErrorMessage)
        .pattern(currencyPattern, invalidCurrencyErrorMessage)
        .addRule((value, data) => {
          if (parseFloat(value) > parseFloat(data.salesOtherPlatforms)) {
            return false;
          }
          return true;
        }, `Deductions can't be more than sales`),
    daysAvailable:
      NumberType(numberErrorMessage)
        .isInteger(intgerErrorMessage)
        .min(0, greaterThanZeroErrorMessage)
        .max(daysInPeriod, `Days available can't be more than ${daysInPeriod}`)
        .isRequired(requiredErrorMessage),
    daysOccupied:
      NumberType(numberErrorMessage)
        .isInteger(intgerErrorMessage)
        .min(0, greaterThanZeroErrorMessage)
        .addRule((value, data) => {
          if (parseFloat(value) > parseFloat(data.daysAvailable)) {
            return false;
          }
          return true;
        }, `Days occupied can't be more than the days available`)
        .isRequired(requiredErrorMessage),
  }

  const handleSaveToRedux = () => {
    const updatedPeriod = { ...period };
    const updatedLicenses = updatedPeriod.licenses.map(lic => {
      if (lic.license === license.license) {
        return {
          ...lic,
          ...formValues
        };
      }
      return lic; // Return the original license if it doesn't match
    }).filter(Boolean); // Filter out any falsy values (null, undefined, etc.)

    updatedPeriod.licenses = updatedLicenses;

    dispatch(updateTaxFlowData({ selectedLicenses: [updatedPeriod] }));
  }

  // Expose saveToRedux function to parent component via ref
  useImperativeHandle(ref, () => ({
    ...ref?.current,
    saveToRedux: () => {
      handleSaveToRedux();
    },
    getFormData: () => {
      return {
        periodStart: startDate,
        periodEnd: endDate,
        license: formValues,
        licenseNum: license.license
      }
    }
  }));

  const handleSubmit = () => {
    handleSaveToRedux();
  }

  const updateFormValues = (values) => {
    let updatedValues = { ...values };

    if (Object.keys(updatedValues).length) {
      const taxableReceiptsOtherPlatforms = calculateTaxableReceipts(updatedValues.salesOtherPlatforms, updatedValues.deductionsOtherPlatforms);
      updatedValues.taxableReceiptsOtherPlatforms = taxableReceiptsOtherPlatforms;

      const taxableReceiptsPlatformsThatRemit = calculateTaxableReceipts(updatedValues.salesPlatformsThatRemit, updatedValues.deductionsPlatformsThatRemit);
      updatedValues.taxableReceiptsPlatformsThatRemit = taxableReceiptsPlatformsThatRemit;
    }

    setFormValues(updatedValues);
  }

  return (
    <Form
      ref={ref}
      fluid
      checkTrigger="none"
      onCheck={(errors) => {
        setFormError(errors);
      }}
      formDefaultValue={formModel}
      onChange={(values) => {
        updateFormValues(values);
      }}
      onSubmit={() => { handleSubmit() }}
    >
      <Grid fluid className="mb-16 report-revenue-input-wrapper">
        <HeaderRow hasCollectionAgreement={hasCollectionAgreement} address={license.address} unitNumber={license.unitNumber} platformsThatRemitLabel={platformsThatRemitLabel} platformsThatDoNotRemitLabel={platformsThatDoNotRemitLabel} />
        <SalesRow
          label={salesLabel}
          description={salesDescription}
          hasCollectionAgreement={hasCollectionAgreement}
          platformsThatRemitLabel={platformsThatRemitLabel}
          platformsThatDoNotRemitLabel={platformsThatDoNotRemitLabel}
          validationModel={validationModel}
          formError={formError} />
        {
          showDeductions && (
            <DeductionsRow
              label={deductionsLabel}
              description={deductionsDescription}
              hasCollectionAgreement={hasCollectionAgreement}
              platformsThatRemitLabel={platformsThatRemitLabel}
              platformsThatDoNotRemitLabel={platformsThatDoNotRemitLabel}
              validationModel={validationModel}
              formError={formError} />
          )
        }
        <DividerRow />
        <TaxableReceiptsRow
          label={taxableReceitpsLabel}
          hasCollectionAgreement={hasCollectionAgreement}
          platformsThatRemitLabel={platformsThatRemitLabel}
          platformsThatDoNotRemitLabel={platformsThatDoNotRemitLabel}
          formValues={formValues} />
        <DividerRow />
        {
          !hidePropertyOccupancy && (
            <PropertyOccupancyRow validationModel={validationModel} formError={formError} />
          )
        }
      </Grid>
    </Form>
  );
});

const CurrencyAddOn = memo(() => {
  return (
    <div>$</div>
  );
});

const currencyAddon = <CurrencyAddOn />;

const DividerRow = () => (
  <Row>
    <Col xs={24}>
      <Divider className="bg-light-gray" style={{ margin: '4px 0' }} />
    </Col>
  </Row>
);

const SectionPlatformNameColumns = ({ hasCollectionAgreement, platformsThatRemitLabel, platformsThatDoNotRemitLabel }) => {
  return (
    hasCollectionAgreement && (
      <Col xs={24} md={12} mdHidden lgHidden xlHidden xxlHidden>
        <FlexboxGrid role="grid" >
          <Row align="bottom">
            <Col xs={12} md={6}>
              <Text className="text-small">{platformsThatRemitLabel}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text className="text-small">{platformsThatDoNotRemitLabel}</Text>
            </Col>
          </Row>
        </FlexboxGrid>
      </Col>
    )
  );
};

const InfoIcon = ({ text }) => (
  <Whisper
    trigger={['hover', 'click']}
    speaker={
      <Tooltip>{text}</Tooltip>
    }
  >
    <InfoRoundIcon style={{ cursor: 'pointer' }} />
  </Whisper>
);

const SalesRow = ({ label, description, hasCollectionAgreement, validationModel, formError, platformsThatRemitLabel, platformsThatDoNotRemitLabel }) => {
  return (
    <Row className="bg-lighter-grey report-revenue-row">
      <Col xs={hasCollectionAgreement ? 24 : 12} md={12}>
        <Stack spacing={10} alignItems="center" className="report-revenue-row-heading">
          <Stack.Item>
            <Text className="sm-bold">{label}</Text>
          </Stack.Item>
          {
            description && (
              <Stack.Item>
                <InfoIcon text={description}></InfoIcon>
              </Stack.Item>
            )
          }
        </Stack>
      </Col>
      <SectionPlatformNameColumns
        hasCollectionAgreement={hasCollectionAgreement}
        platformsThatRemitLabel={platformsThatRemitLabel}
        platformsThatDoNotRemitLabel={platformsThatDoNotRemitLabel} />
      <Col xs={12} md={6} xsHidden={!hasCollectionAgreement} smHidden={!hasCollectionAgreement} mdHidden={false} lgHidden={false} xlHidden={false} xxlHidden={false}>
        {
          hasCollectionAgreement && (
            <FormTextField
              name='salesPlatformsThatRemit'
              trimStartOnChange={true}
              trimEndOnChange={true}
              validationRule={validationModel.salesPlatformsThatRemit}
              hasError={formError['salesPlatformsThatRemit']}
              errorMessage={formError['salesPlatformsThatRemit']}
              inputGroupAddOnStart={currencyAddon}
              inputFieldType="number"
              inputMode="decimal"
            />
          )
        }
      </Col>
      <Col xs={12} md={6}>
        <FormTextField
          name='salesOtherPlatforms'
          trimStartOnChange={true}
          trimEndOnChange={true}
          validationRule={validationModel.salesOtherPlatforms}
          hasError={formError['salesOtherPlatforms']}
          errorMessage={formError['salesOtherPlatforms']}
          inputGroupAddOnStart={currencyAddon}
          inputFieldType="number"
          inputMode="decimal"
        />
      </Col>
    </Row>
  );
};

const DeductionsRow = ({ label, description, hasCollectionAgreement, validationModel, formError, platformsThatRemitLabel, platformsThatDoNotRemitLabel }) => {
  return (
    <Row className="report-revenue-row">
      <Col xs={hasCollectionAgreement ? 24 : 12} md={12}>
        <Stack spacing={10} className="report-revenue-row-heading">
          <Stack.Item>
            <Text className="sm-bold">{label}</Text>
          </Stack.Item>
          {
            description && (
              <Stack.Item>
                <InfoIcon text={description}></InfoIcon>
              </Stack.Item>
            )
          }
        </Stack>
      </Col>
      <SectionPlatformNameColumns
        hasCollectionAgreement={hasCollectionAgreement}
        platformsThatRemitLabel={platformsThatRemitLabel}
        platformsThatDoNotRemitLabel={platformsThatDoNotRemitLabel} />
      <Col xs={12} md={6} xsHidden={!hasCollectionAgreement} smHidden={!hasCollectionAgreement} mdHidden={false} lgHidden={false} xlHidden={false} xxlHidden={false}>
        {
          hasCollectionAgreement && (
            <FormTextField
              name='deductionsPlatformsThatRemit'
              trimStartOnChange={true}
              trimEndOnChange={true}
              validationRule={validationModel.deductionsPlatformsThatRemit}
              hasError={formError['deductionsPlatformsThatRemit']}
              errorMessage={formError['deductionsPlatformsThatRemit']}
              inputGroupAddOnStart={currencyAddon}
              inputFieldType="number"
              inputMode="decimal"
            />
          )
        }
      </Col>
      <Col xs={12} md={6}>
        <FormTextField
          name='deductionsOtherPlatforms'
          trimStartOnChange={true}
          trimEndOnChange={true}
          validationRule={validationModel.deductionsOtherPlatforms}
          hasError={formError['deductionsOtherPlatforms']}
          errorMessage={formError['deductionsOtherPlatforms']}
          inputGroupAddOnStart={currencyAddon}
          inputFieldType="number"
          inputMode="decimal"
        />
      </Col>
    </Row>
  );
};

const TaxableReceiptsRow = ({ label, hasCollectionAgreement, formValues, platformsThatRemitLabel, platformsThatDoNotRemitLabel }) => {
  return (
    <Row className="report-revenue-row aling-items-stretch">
      <Col xs={hasCollectionAgreement ? 24 : 12} md={12}>
        <Text className={`text-bold report-revenue-row-heading`}>{label}</Text>
      </Col>
      <SectionPlatformNameColumns
        hasCollectionAgreement={hasCollectionAgreement}
        platformsThatRemitLabel={platformsThatRemitLabel}
        platformsThatDoNotRemitLabel={platformsThatDoNotRemitLabel} />
      <Col xs={12} md={6} xsHidden={!hasCollectionAgreement} smHidden={!hasCollectionAgreement} mdHidden={false} lgHidden={false} xlHidden={false} xxlHidden={false}>{
        hasCollectionAgreement && (
          <Text className="report-revenue-calculated-value" name='taxableReceiptsPlatformsThatRemit'>{convertNumberToCurrency(formValues?.taxableReceiptsPlatformsThatRemit)}</Text>
        )
      }</Col>
      <Col xs={12} md={6}>
        <Text className="report-revenue-calculated-value" name='taxableReceiptsOtherPlatforms'>{convertNumberToCurrency(formValues?.taxableReceiptsOtherPlatforms)}</Text>
      </Col>
    </Row>
  );
};

const PropertyOccupancyRow = ({ validationModel, formError }) => {
  return (
    <Row className="report-revenue-row">
      <Col xs={24} md={12}>
        <Text className={`text-bold report-revenue-row-heading`}>Property Occupancy</Text>
      </Col>
      <Col xs={12} md={6}>
        <FormTextField
          name="daysAvailable"
          trimStartOnChange={true}
          trimEndOnChange={true}
          label="No. Days Available"
          validationRule={validationModel.daysAvailable}
          hasError={formError['daysAvailable']}
          errorMessage={formError['daysAvailable']}
          labelClass="text-small"
          inputFieldType="number"
          inputMode="numeric"
        />
      </Col>
      <Col xs={12} md={6}>
        <FormTextField
          name="daysOccupied"
          trimStartOnChange={true}
          trimEndOnChange={true}
          label="No. Days Occupied"
          validationRule={validationModel.daysOccupied}
          hasError={formError['daysOccupied']}
          errorMessage={formError['daysOccupied']}
          labelClass="text-small"
          inputFieldType="number"
          inputMode="numeric"
        />
      </Col>
    </Row>
  );
};

const HeaderRow = ({ hasCollectionAgreement, address, unitNumber, platformsThatRemitLabel, platformsThatDoNotRemitLabel }) => {
  const fullAddress = getAddressWithUnitNumber(address, unitNumber);
  return (
    <FlexboxGrid as={Row} className="mb-12" align="bottom">
      <FlexboxGrid.Item as={Col} xs={24} md={12}>
        <Text className={`text-bold text-large mb-4`}>{fullAddress}</Text>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} xs={12} md={6} xsHidden={hasCollectionAgreement} smHidden={hasCollectionAgreement} mdHidden={false} lgHidden={false} xlHidden={false} xxlHidden={false}>
        {
          hasCollectionAgreement && (
            <Text className="text-small">{platformsThatRemitLabel}</Text>
          )
        }
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} xs={12} md={6} xsHidden={hasCollectionAgreement} smHidden={hasCollectionAgreement} mdHidden={false} lgHidden={false} xlHidden={false} xxlHidden={false}>
        <Text className="text-small">{platformsThatDoNotRemitLabel}</Text>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default ReportRevenueInput;
